import {
  ILead,
  ILeadDraft,
  IRouterPaginationResponse,
  IUploadedLeadDocument,
  LeadsQuery,
} from "interfaces";
import { ClientRegistryMatch, LeadIndustryFlag } from "interfaces/enums";
import { buildUrlWithFilters } from "../../utils";
import { complianceApi } from "./index";
import { institutionalApi } from "../institutional";
import {
  HIGH_RISK_INDUSTRIES,
  PROHIBITED_INDUSTRIES,
} from "pages/leads/industries";

// ---------------------------------------------------------------------------
// Leads CRM — served by the compliance engine (see api/compliance/index.tsx).
//
//   GET    /api/leads                 — paginated list (status/search/sort)
//   GET    /api/leads/registry_check  — pre-lead duplicate check ({ match })
//   POST   /api/leads                 — create (server derives industry_flag)
//   PATCH  /api/leads/{id}            — update / advance status; the server
//                                       rejects READY (422) unless the
//                                       qualification package is complete
//   POST   /api/leads/{id}/mint_link  — only from READY (409 otherwise);
//                                       AiPrise leg is MOCKED server-side
//                                       until the engine holds credentials
//
// Broker scoping is NOT enforced yet: the engine has no caller identity, so
// `endpoint`/`group_id` travel as plain query params. Real scoping arrives
// when the institutional backend implements /leads (token-derived, like
// /kyb/*) and fans out to the engine's /webhooks/lead.
// ---------------------------------------------------------------------------

// Fields a lead must have before it can be marked READY (the gate that
// unlocks onboarding-link creation). Mirrors the server's enforcement —
// used to hint the missing fields in the UI before the request is made.
export const LEAD_REQUIRED_FIELDS: Array<keyof ILead> = [
  "company_name",
  "contact_name",
  "email",
  "phone_number",
  // Canonical id of the whole flow — must exist before the KYB handoff.
  "rfc",
  "industry",
  "product",
  "corridors",
  "estimated_monthly_volume_range",
  // Declared transactional profile — form data, not a document (KB v0.5
  // dropped the perfil_transaccional upload), so the fields gate READY.
  "operation_frequency",
  "expected_counterparties_range",
  "funds_origin",
  "funds_destination",
];

export const getMissingLeadFields = (lead: ILeadDraft): Array<keyof ILead> =>
  LEAD_REQUIRED_FIELDS.filter((field) => {
    const value = lead[field];
    if (Array.isArray(value)) return value.length === 0;
    return value === undefined || value === null || value === "";
  });

// Compliance pre-screen: a UX hint only. The engine RE-derives the real flag
// from its KB-synced giros config and never trusts this value.
export const deriveIndustryFlag = (
  industry?: string,
): LeadIndustryFlag | null => {
  if (!industry) return null;
  if (PROHIBITED_INDUSTRIES.includes(industry))
    return LeadIndustryFlag.PROHIBITED;
  if (HIGH_RISK_INDUSTRIES.includes(industry))
    return LeadIndustryFlag.HIGH_RISK;
  return null;
};

// One onboarding document, as served by the engine's KB-synced config.
// `optional` marks conditional items ("si la posee") — collected when they
// exist, never a blocker.
export interface IDocumentRequirement {
  id: string;
  label: string;
  optional?: boolean;
  // The engine serves an official fillable template for this item
  // (GET /api/leads/document_templates/{id}) — download, fill, sign,
  // upload back into the same slot.
  template?: boolean;
}

// A pre-check verdict of "wrong document" strips the upload's ability to
// satisfy its requirement (mirror of the server gate). No verdict — agent
// inactive, unreviewable file — never counts as failure.
export const isReviewFailed = (doc?: IUploadedLeadDocument): boolean =>
  doc?.review?.verdict?.matches_requested === false;

// A failed verdict counts against the package unless an ADMIN override
// (server-audited) waived it.
export const isEffectivelyFailed = (doc?: IUploadedLeadDocument): boolean =>
  isReviewFailed(doc) && !doc?.override;

export const getMissingDocuments = (
  lead: ILeadDraft,
  requirements: Array<IDocumentRequirement> | undefined,
  crossSatisfaction?: Record<string, string[]>,
): Array<IDocumentRequirement> => {
  // Satisfied ONLY by an uploaded file the pre-check did not reject
  // (2026-08-12): no attestation, no override — a wrong file must be
  // replaced. An un-rejected upload also satisfies the items it stands in
  // for by policy (2026-08-27: acta -> poder notarial etc., served in the
  // requirements' cross_satisfaction map). Mirror of the server gate.
  const uploads = lead.documents_uploaded ?? {};
  const satisfied = new Set(
    Object.keys(uploads).filter((id) => !isEffectivelyFailed(uploads[id])),
  );
  satisfied.forEach((id) =>
    (crossSatisfaction?.[id] ?? []).forEach((covered) =>
      satisfied.add(covered),
    ),
  );
  return (requirements ?? []).filter(
    (doc) => !doc.optional && !satisfied.has(doc.id),
  );
};

// coveredId -> the uploaded document standing in for it (e.g.
// poder_notarial -> acta_constitutiva), for the "covered by" slot state.
export const getCoveredDocuments = (
  lead: ILeadDraft,
  crossSatisfaction?: Record<string, string[]>,
): Record<string, string> => {
  const uploads = lead.documents_uploaded ?? {};
  const covered: Record<string, string> = {};
  Object.keys(uploads)
    .filter((id) => !isEffectivelyFailed(uploads[id]))
    .forEach((id) =>
      (crossSatisfaction?.[id] ?? []).forEach((coveredId) => {
        covered[coveredId] = id;
      }),
    );
  return covered;
};

export const MAX_DOCUMENT_BYTES = 15 * 1024 * 1024; // mirror of the server cap

// Downloads go through fetch (the x-api-key header can't ride a plain <a>);
// the blob is handed to the browser as an object URL.
export const openLeadDocument = async (leadId: string, docId: string) => {
  const { getPath } = await import("../customFetchBase");
  const { store } = await import("../../app/store");
  const token = store.getState()?.auth?.token;
  const response = await fetch(
    `${getPath("compliance")}/api/leads/${leadId}/documents/${docId}`,
    {
      headers: {
        "x-api-key": process.env.REACT_APP_COMPLIANCE_API_KEY ?? "",
        ...(token ? { authorization: token } : {}),
      },
    },
  );
  if (!response.ok) throw new Error("Document download failed");
  const url = URL.createObjectURL(await response.blob());
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// Official fillable template for a checklist item (docx can't render in a
// tab, so this saves the file instead of opening it). The filename comes
// from the server's Content-Disposition.
export const downloadLeadDocumentTemplate = async (docId: string) => {
  const { getPath } = await import("../customFetchBase");
  const { store } = await import("../../app/store");
  const token = store.getState()?.auth?.token;
  const response = await fetch(
    `${getPath("compliance")}/api/leads/document_templates/${docId}`,
    {
      headers: {
        "x-api-key": process.env.REACT_APP_COMPLIANCE_API_KEY ?? "",
        ...(token ? { authorization: token } : {}),
      },
    },
  );
  if (!response.ok) throw new Error("Template download failed");
  const match = /filename\*=UTF-8''([^;]+)/.exec(
    response.headers.get("content-disposition") ?? "",
  );
  const filename = match ? decodeURIComponent(match[1]) : `${docId}.docx`;
  const url = URL.createObjectURL(await response.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const leadsApi = complianceApi.injectEndpoints({
  endpoints: (builder) => ({
    getDocumentRequirements: builder.query<
      {
        version: string;
        items: Array<IDocumentRequirement>;
        // key item -> items it stands in for once uploaded and not
        // rejected (same map the server gate credits from)
        cross_satisfaction?: Record<string, string[]>;
      },
      { client_type?: string } | void
    >({
      query: (params) => ({
        url: `/api/leads/document_requirements`,
        params: params ?? undefined,
      }),
    }),
    getAllLeads: builder.query<IRouterPaginationResponse<ILead>, LeadsQuery>({
      query: (props) => ({
        url: buildUrlWithFilters(`/api/leads`, props),
      }),
      providesTags: ["Leads"],
    }),
    checkClientRegistry: builder.query<
      { match: ClientRegistryMatch },
      { company_name?: string; email?: string; rfc?: string }
    >({
      query: (params) => ({
        url: `/api/leads/registry_check`,
        params,
      }),
      // Funnel state feeds this answer, so any lead mutation must invalidate it
      // — otherwise the "already onboarded" notice lags behind reality.
      providesTags: ["ClientRegistry"],
    }),
    createLead: builder.mutation<ILead, ILeadDraft>({
      query: (draft) => ({
        url: `/api/leads`,
        method: "POST",
        body: draft,
      }),
      invalidatesTags: ["Leads", "ClientRegistry"],
    }),
    updateLead: builder.mutation<ILead, ILeadDraft & { id: string }>({
      query: ({ id, ...changes }) => ({
        url: `/api/leads/${id}`,
        method: "PATCH",
        body: changes,
      }),
      invalidatesTags: ["Leads", "ClientRegistry"],
    }),
    overrideLeadDocument: builder.mutation<
      ILead,
      { id: string; docId: string; actor: string; rationale: string }
    >({
      // ADMIN-only in the UI (user.is_admin gates the control); the engine
      // audits actor + rationale and binds the waiver to the file's sha.
      query: ({ id, docId, actor, rationale }) => ({
        url: `/api/leads/${id}/documents/${docId}/override`,
        method: "POST",
        body: { actor, rationale },
      }),
      invalidatesTags: ["Leads"],
    }),
    uploadLeadDocument: builder.mutation<
      ILead,
      { id: string; docId: string; file: File }
    >({
      // Raw-body upload: the file IS the body, its name rides ?filename=,
      // its type the Content-Type header (see complianceFetchBase — binary
      // bodies bypass the JSON cleaning).
      query: ({ id, docId, file }) => ({
        url: `/api/leads/${id}/documents/${docId}`,
        method: "POST",
        params: { filename: file.name },
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      }),
      invalidatesTags: ["Leads"],
    }),
    mintOnboardingLink: builder.mutation<ILead, { id: string }>({
      query: ({ id }) => ({
        url: `/api/leads/${id}/mint_link`,
        method: "POST",
      }),
      invalidatesTags: ["Leads", "ClientRegistry"],
      // "Onboardings" lives on the institutional api slice — cross-slice
      // invalidation so the onboardings page refetches after the handoff.
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(institutionalApi.util.invalidateTags(["Onboardings"]));
        } catch {
          // mint failed — nothing to refetch
        }
      },
    }),
  }),
});

export const {
  useGetDocumentRequirementsQuery,
  useUploadLeadDocumentMutation,
  useOverrideLeadDocumentMutation,
  useGetAllLeadsQuery,
  useCreateLeadMutation,
  useUpdateLeadMutation,
  useMintOnboardingLinkMutation,
  useCheckClientRegistryQuery,
} = leadsApi;
